import { useMemo, useState } from 'react'
import { ArrowsClockwise, Gavel, Wrench } from '@phosphor-icons/react'
import { displayName } from '@/auth/authApi'
import { useAuth, useCan } from '@/auth/context'
import { Banner, ChoiceGroup, Field, Input, Select, TextArea } from '@/ui'
import {
  BALANCE_KIND_LABEL,
  CAUSE_LABEL,
  CORRECTION_KIND_LABEL,
  CORRECTION_KINDS,
  DECISION_LABEL,
  RUN_CAUSES,
  bpText,
  useBalanceCheck,
  useBandHistory,
  useBands,
  useRunCorrection,
  useRunDecision,
  type BandRow,
  type RunDetail,
  type RunProblem,
  type CorrectionKind,
  type RunCause,
  type RunDecision,
} from '../../api/process'
import { useSackTypes } from '../../api/setup'
import { exceptionLabel } from '../../labels'
import { serverProblem } from '../../model/serverError'
import {
  correctionBody,
  correctionFields,
  correctionOk,
  emptyCorrectionDraft,
  validateCorrection,
  type CorrectionDraft,
} from '../../model/process/correctionForm'
import { decisionOk, decisionOptions, ownRunReason, validateDecision, type DecisionDraft } from '../../model/process/runDecision'
import { ActionButton } from '../Can'
import { FormDialog } from '../FormDialog'
import { Kg } from '../bits'
import { bandText } from '../../model/process/format'

/**
 * Investigate a run that does not balance: record corrections, re-check the balance, then decide
 * how the problem closes. Only a supervisor who neither ran the machine nor did its cleanout may
 * act (the server refuses the others with 403 and we show its message).
 */
export function RunInvestigation({ run, problem }: { run: RunDetail; problem: RunProblem }) {
  const { user } = useAuth()
  const can = useCan()
  const [dialog, setDialog] = useState<'correct' | 'recheck' | 'decide' | null>(null)
  const own = ownRunReason(user?.id, run)
  const inException = run.status === 'EXCEPTION'
  const notException = inException ? null : 'The run is not waiting for a decision.'
  const kinds = run.balance.kinds.length ? run.balance.kinds : (problem.detail.kinds as string[] | undefined) ?? []

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-1.5 text-[13.5px]">
        <span className="text-muted">Why:</span>
        {kinds.length ? kinds.map((k) => <span key={k} className="font-semibold text-ink">{BALANCE_KIND_LABEL[k] ?? k}</span>) : '—'}
      </div>
      {own && <Banner tone="warn">{own}</Banner>}
      <div className="flex flex-wrap gap-2">
        <ActionButton perm="process-runs:correct" size="sm" variant="secondary" leadingIcon={<Wrench />} blockedReason={own ?? notException} onClick={() => setDialog('correct')}>
          Record a correction
        </ActionButton>
        <ActionButton perm="process-runs:correct" size="sm" variant="secondary" leadingIcon={<ArrowsClockwise />} blockedReason={own ?? notException} onClick={() => setDialog('recheck')}>
          Re-check balance
        </ActionButton>
        <ActionButton
          perm="exceptions:resolve"
          size="sm"
          leadingIcon={<Gavel />}
          blockedReason={own ?? notException ?? (!can('process-runs:correct') ? 'Only a supervisor who may correct runs decides a run problem.' : null)}
          onClick={() => setDialog('decide')}
        >
          Decide
        </ActionButton>
      </div>
      {dialog === 'correct' && <CorrectionDialog run={run} problem={problem} onClose={() => setDialog(null)} />}
      {dialog === 'recheck' && <RecheckDialog run={run} onClose={() => setDialog(null)} />}
      {dialog === 'decide' && <DecisionDialog run={run} problem={problem} onClose={() => setDialog(null)} />}
    </div>
  )
}

function ServerRefusal({ error }: { error: unknown }) {
  if (!error) return null
  const p = serverProblem(error)
  return (
    <Banner tone="error" title={p.message}>
      {p.details.length > 0 && p.details.join(' · ')}
    </Banner>
  )
}

// ---------------------------------------------------------------- correction

function CorrectionDialog({ run, problem, onClose }: { run: RunDetail; problem: RunProblem; onClose: () => void }) {
  const [d, setD] = useState<CorrectionDraft>(emptyCorrectionDraft({ exceptionId: problem.id }))
  const [touched, setTouched] = useState(false)
  const save = useRunCorrection(run.id, () => true)
  const sackTypes = useSackTypes({ page: 1, limit: 200, active: true })
  const f = correctionFields(d.kind)
  const sacks = run.outputs.flatMap((o) =>
    o.sacks.filter((s) => s.status !== 'VOID').map((s) => ({ ...s, unitCode: o.code, outputLabel: o.streamKey })),
  )
  const streamKeys = [...run.step.outputs.map((o) => o.key), 'FLUSH']
  const outputLabel = (key: string | null) =>
    key === 'FLUSH' ? 'Flush' : (run.step.outputs.find((o) => o.key === key)?.label ?? key ?? '')
  const picked = sacks.find((s) => s.containerId === d.containerId)
  const sackType = sackTypes.data?.rows.find((t) => t.id === d.sackTypeId)
  const tareG = sackType ? sackType.tareG : (picked?.tareG ?? null)
  const p = validateCorrection(d, { tareG, streamKeys })
  const shown = (k: keyof CorrectionDraft) => (touched ? p[k] : undefined)
  const openProblems = run.problems.filter((x) => x.status === 'OPEN')

  return (
    <FormDialog
      open
      onClose={onClose}
      title={`Record a correction · ${run.code}`}
      description="Fix what was recorded wrong. The balance is checked again straight after; the run stays waiting for a decision."
      saveLabel="Save correction"
      loading={save.isPending}
      canSave={!touched || correctionOk(p)}
      onSave={() => {
        setTouched(true)
        if (!correctionOk(p)) return Promise.reject(new Error('invalid'))
        return save.mutateAsync(correctionBody(d))
      }}
    >
      {save.isError && <ServerRefusal error={save.error} />}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Problem" required error={shown('exceptionId')}>
          <Select
            value={d.exceptionId}
            onChange={(exceptionId) => setD({ ...d, exceptionId })}
            options={openProblems.map((x) => ({ value: x.id, label: exceptionLabel(x.type).en }))}
          />
        </Field>
        <Field label="Cause" required error={shown('reasonCode')}>
          <Select<RunCause>
            value={d.reasonCode}
            onChange={(reasonCode) => setD({ ...d, reasonCode })}
            placeholder="Choose…"
            options={RUN_CAUSES.map((c) => ({ value: c, label: CAUSE_LABEL[c] }))}
          />
        </Field>
      </div>
      <Field label="What to correct" required error={shown('kind')}>
        <Select<CorrectionKind>
          value={d.kind}
          onChange={(kind) => setD({ ...d, kind })}
          placeholder="Choose…"
          options={CORRECTION_KINDS.map((k) => ({ value: k, label: CORRECTION_KIND_LABEL[k] }))}
        />
      </Field>
      {f.pickSack && (
        <Field label="Output sack" required error={shown('containerId')}>
          <Select
            value={d.containerId}
            onChange={(containerId) => setD({ ...d, containerId })}
            placeholder="Choose…"
            options={sacks.map((s) => ({
              value: s.containerId,
              label: `${s.tag ?? s.conflictTag ?? 'No tag'} · ${outputLabel(s.outputLabel)} · ${s.netG !== null ? `${(s.netG / 1000).toFixed(3)} kg` : '—'}`,
            }))}
          />
        </Field>
      )}
      {f.streamKey && (
        <Field label="Which output" required error={shown('streamKey')}>
          <Select
            value={d.streamKey}
            onChange={(streamKey) => setD({ ...d, streamKey })}
            placeholder="Choose…"
            options={streamKeys.map((k) => ({ value: k, label: outputLabel(k) }))}
          />
        </Field>
      )}
      {f.tag && (
        <Field
          label={d.kind === 'ADD_INPUT' ? 'Bag tag' : 'New sack tag'}
          required
          error={shown('qrPayload')}
          hint={d.kind === 'ADD_INPUT' ? 'The tag of the bag that went in without a scan.' : 'A sack tag from the site roll.'}
        >
          <Input value={d.qrPayload} onChange={(e) => setD({ ...d, qrPayload: e.target.value })} maxLength={64} className="font-mono" />
        </Field>
      )}
      {(f.sackType || f.weight) && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {f.sackType && (
            <Field label="Sack type" required={f.sackTypeRequired} error={shown('sackTypeId')} hint={f.sackTypeRequired ? undefined : 'Only if the sack was changed.'}>
              <Select
                value={d.sackTypeId}
                onChange={(sackTypeId) => setD({ ...d, sackTypeId })}
                placeholder={f.sackTypeRequired ? 'Choose…' : 'Same sack'}
                options={(sackTypes.data?.rows ?? []).map((t) => ({ value: t.id, label: `${t.name} (${t.code}) · ${t.tareKg} kg` }))}
              />
            </Field>
          )}
          {f.weight && (
            <Field
              label="Scale reading"
              required={f.weightRequired}
              error={shown('weightKg')}
              hint={d.kind === 'ADD_INPUT' ? 'Only if the bag was never weighed at issue.' : 'What the scale shows, with the sack.'}
            >
              <Input inputMode="decimal" value={d.weightKg} onChange={(e) => setD({ ...d, weightKg: e.target.value })} trailing="kg" className="font-mono" />
            </Field>
          )}
        </div>
      )}
      {d.kind === 'VOID_OUTPUT' && <p className="text-[13.5px] text-muted">The sack and its tag become void. Its kilos leave the balance.</p>}
    </FormDialog>
  )
}

// ---------------------------------------------------------------- re-check

/** Newer range versions of this crop / step that fit the machine (its own rows, or generic ones). */
function useNewerBands(run: RunDetail) {
  const q = useBands({ cropId: run.cropId ?? undefined, stepCode: run.stepCode, page: 1, limit: 200 }, Boolean(run.cropId))
  return useMemo(() => {
    const current = run.balance.band
    return (q.data?.rows ?? []).filter(
      (b: BandRow) => b.id !== current?.id && !b.cropVarietyId && (b.equipmentId === null || b.equipmentId === run.equipmentId),
    )
  }, [q.data, run])
}

function RecheckDialog({ run, onClose }: { run: RunDetail; onClose: () => void }) {
  const [bandId, setBandId] = useState('')
  const check = useBalanceCheck(run.id, () => true)
  const bands = useNewerBands(run)
  return (
    <FormDialog
      open
      onClose={onClose}
      title={`Re-check balance · ${run.code}`}
      description="Checks the balance again with the corrections made so far. The run stays waiting for a decision either way."
      saveLabel="Re-check"
      loading={check.isPending}
      onSave={() => check.mutateAsync({ recheckBandId: bandId || undefined })}
    >
      {check.isError && <ServerRefusal error={check.error} />}
      <p className="text-[14px]">
        Range now: <span className="font-mono">{bandText(run.balance.band)}</span>
        {run.balance.band?.version != null && <span className="text-muted"> · version {run.balance.band.version}</span>}
      </p>
      <Field
        label="Check against"
        hint="Only choose another version on purpose, when the machine's range itself was wrong. Both results stay on record."
      >
        <Select
          value={bandId}
          onChange={setBandId}
          options={[
            { value: '', label: 'The range the run used' },
            ...bands.map((b) => ({
              value: b.id,
              label: `Version ${b.version} · ${bandText(b)} · from ${b.effectiveFrom ?? '—'}${b.equipmentId ? '' : ' · all machines'}`,
            })),
          ]}
        />
      </Field>
    </FormDialog>
  )
}

// ---------------------------------------------------------------- decide

function DecisionDialog({ run, problem, onClose }: { run: RunDetail; problem: RunProblem; onClose: () => void }) {
  const { user } = useAuth()
  const [d, setD] = useState<DecisionDraft>({ cause: '', decision: '', note: '' })
  const [bandId, setBandId] = useState('')
  const [touched, setTouched] = useState(false)
  const decide = useRunDecision(() => true)
  const bands = useNewerBands(run)
  const history = useBandHistory(
    run.cropId ? { cropId: run.cropId, stepCode: run.stepCode, equipmentId: run.equipmentId ?? undefined, limit: 10 } : null,
  )
  const options = decisionOptions(run.balance, d.cause)
  const p = validateDecision(d, options)
  const shown = (k: keyof DecisionDraft) => (touched ? p[k] : undefined)
  const otherOpen = run.problems.filter((x) => x.status === 'OPEN' && x.id !== problem.id).length

  return (
    <FormDialog
      open
      onClose={onClose}
      title={`Decide · ${run.code}`}
      description="Close the problem: balanced after correction, the shortfall accepted with its reason, or every output moved to ordinary."
      saveLabel="Close problem"
      loading={decide.isPending}
      canSave={!touched || decisionOk(p)}
      onSave={() => {
        setTouched(true)
        if (!decisionOk(p)) return Promise.reject(new Error('invalid'))
        return decide.mutateAsync({
          exceptionId: problem.id,
          cause: d.cause as RunCause,
          decision: d.decision as RunDecision,
          note: d.note.trim() || undefined,
          recheckBandId: d.decision === 'CORRECTED' && bandId ? bandId : undefined,
        })
      }}
    >
      {decide.isError && <ServerRefusal error={decide.error} />}
      <Field label="Cause" required error={shown('cause')} hint="What the investigation found.">
        <Select<RunCause>
          value={d.cause}
          onChange={(cause) => setD({ ...d, cause })}
          placeholder="Choose…"
          options={RUN_CAUSES.map((c) => ({ value: c, label: CAUSE_LABEL[c] }))}
        />
      </Field>
      <Field label="Decision" required error={shown('decision')}>
        <ChoiceGroup<RunDecision>
          layout="stack"
          value={d.decision || null}
          onChange={(decision) => setD({ ...d, decision })}
          options={options.map((o) => ({
            value: o.value,
            disabled: !o.allowed,
            tone: o.value === 'MOVE_TO_CONV' ? 'warn' : 'default',
            label: (
              <span className="flex flex-col text-left">
                <span>{DECISION_LABEL[o.value]}</span>
                {o.reason && <span className="text-[12px] font-normal normal-case text-muted">{o.reason}</span>}
              </span>
            ),
          }))}
        />
      </Field>
      {d.decision === 'MOVE_TO_CONV' && (
        <Banner tone="warn">The run, every output and every sack become ordinary (red tie). This cannot be undone.</Banner>
      )}
      {d.decision === 'CORRECTED' && bands.length > 0 && (
        <Field label="Re-check against" hint="Only when the machine's range itself was wrong.">
          <Select
            value={bandId}
            onChange={setBandId}
            options={[
              { value: '', label: 'The range the run used' },
              ...bands.map((b) => ({ value: b.id, label: `Version ${b.version} · ${bandText(b)} · from ${b.effectiveFrom ?? '—'}` })),
            ]}
          />
        </Field>
      )}
      <Field
        label={d.decision === 'ACCEPT_SHORTFALL' ? 'Reason and process fix' : 'Process fix'}
        required={d.decision === 'ACCEPT_SHORTFALL'}
        error={shown('note')}
        hint="For example: weigh every sack before it leaves the machine."
      >
        <TextArea rows={2} value={d.note} onChange={(e) => setD({ ...d, note: e.target.value })} maxLength={500} />
      </Field>
      <div className="grid grid-cols-1 gap-3 rounded-card border border-rule bg-paper-tint/40 px-3 py-2.5 text-[13.5px] sm:grid-cols-2">
        <div>
          <div className="font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">Range</div>
          <div>
            {bandText(run.balance.band)}
            {run.balance.band?.version != null && <span className="text-muted"> · version {run.balance.band.version}</span>}
          </div>
          <div className="text-[12px] text-muted">A new version is loaded on the Yield bands page.</div>
        </div>
        <div>
          <div className="font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">Approver</div>
          <div>{user ? displayName(user) : '—'}</div>
          <div className="text-[12px] text-muted">You, recorded on the problem.</div>
        </div>
      </div>
      <div>
        <div className="mb-1 font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">Yield history · this machine and crop</div>
        {history.isLoading ? (
          <p className="text-[13px] text-muted">Loading…</p>
        ) : (history.data ?? []).length === 0 ? (
          <p className="text-[13px] text-muted">No earlier runs.</p>
        ) : (
          <table className="w-full text-[13px]">
            <caption className="sr-only">Recent runs</caption>
            <thead>
              <tr className="text-left font-mono text-[10px] uppercase tracking-label text-muted">
                <th scope="col" className="py-1">Run</th>
                <th scope="col" className="py-1 text-right">In</th>
                <th scope="col" className="py-1 text-right">Yield</th>
                <th scope="col" className="py-1 text-right">Left over</th>
              </tr>
            </thead>
            <tbody>
              {(history.data ?? []).map((h) => (
                <tr key={h.runId} className="border-t border-rule/50">
                  <td className="py-1 font-mono">{h.runCode}</td>
                  <td className="py-1 text-right"><Kg grams={h.inputG} /></td>
                  <td className="py-1 text-right font-mono">{bpText(h.yieldBp)}</td>
                  <td className="py-1 text-right font-mono">{bpText(h.residualBp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {otherOpen > 0 && (
        <p className="text-[13px] text-muted">
          {otherOpen} other {otherOpen === 1 ? 'problem is' : 'problems are'} still open on this run: the batches stay on hold until those are closed too.
        </p>
      )}
    </FormDialog>
  )
}
